import { useAuth } from '../context/AuthContext';
import { useLoginModal } from '../components/LoginModalProvider';
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";

/* ── Fonts ── */
const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

/* ── Tokens ── */
const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g200: "#a7d9bc",
  g100: "#d6f0e3", g50: "#eef8f3",
  r500: "#c0392b", r100: "#fdecea",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n400: "#8a93a8", n200: "#d1d5e0", n150: "#e4e7ef", n100: "#eef0f5",
  n50: "#f7f8fa", white: "#ffffff",
  serif: "'Instrument Serif', Georgia, serif",
  sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

/* ── Icons ── */
const Icon = ({ name, size = 20, color = "currentColor", strokeWidth = 1.6 }) => {
  const paths = {
    ArrowLeft: <><path d="M19 12H5" /><path d="m12 19-7-7 7-7" /></>,
    Flag: <><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" x2="4" y1="22" y2="15" /></>,
    CircleCheck: <><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></>,
    AlertTriangle: <><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3z" /><path d="M12 9v4" /><path d="M12 17h.01" /></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ── Report reasons ── */
const REASONS = [
  { id: "no_show",       label: "Did not show up",          desc: "The professional booked a job but never arrived." },
  { id: "poor_quality",  label: "Poor quality of work",     desc: "The service delivered did not meet the agreed standard." },
  { id: "overcharging",  label: "Overcharging",             desc: "I was charged more than the agreed price." },
  { id: "harassment",    label: "Harassment or misconduct", desc: "I experienced inappropriate behaviour." },
  { id: "fraud",         label: "Fraud or scam",            desc: "I believe this professional is acting fraudulently." },
  { id: "fake_profile",  label: "Fake profile",             desc: "The professional's identity or credentials appear to be false." },
  { id: "other",         label: "Other",                    desc: "Something else not covered above." },
];

/* ── Radio card ── */
function ReasonCard({ reason, selected, onSelect }) {
  const active = selected === reason.id;
  return (
    <button
      type="button"
      onClick={() => onSelect(reason.id)}
      style={{
        width: "100%", textAlign: "left", cursor: "pointer",
        padding: "14px 16px", borderRadius: 12,
        border: `1.5px solid ${active ? T.g500 : T.n150}`,
        background: active ? T.g50 : T.white,
        display: "flex", alignItems: "flex-start", gap: 12,
        transition: "border-color .15s, background .15s",
        outline: "none",
      }}>
      {/* Radio dot */}
      <div style={{
        width: 18, height: 18, borderRadius: "50%", flexShrink: 0, marginTop: 1,
        border: `2px solid ${active ? T.g500 : T.n200}`,
        background: active ? T.g500 : T.white,
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "border-color .15s, background .15s",
      }}>
        {active && <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.white }} />}
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.n800, lineHeight: 1.3 }}>{reason.label}</div>
        <div style={{ fontSize: 13, color: T.n400, marginTop: 2, lineHeight: 1.5 }}>{reason.desc}</div>
      </div>
    </button>
  );
}

/* ── Main ── */
export default function ReportPage() {
  useFonts();
  const navigate = useNavigate();
  const { proId } = useParams();
  const { user } = useAuth();
  const { open: openLogin } = useLoginModal();

  const [proName, setProName] = useState("");
  const [loadingPro, setLoadingPro] = useState(!!proId);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  /* fetch pro's display name */
  useEffect(() => {
    if (!proId) return;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", proId)
        .single();
      setProName(data?.full_name || "this professional");
      setLoadingPro(false);
    })();
  }, [proId]);

  useEffect(() => {
    if (!user) openLogin({ redirect: window.location.pathname });
  }, [user]);

  const CHAR_LIMIT = 1000;
  const detailsOk = details.trim().length >= 20;
  const canSubmit = reason && detailsOk && !loading;

  const handleSubmit = async () => {
    if (!user) { openLogin({ redirect: window.location.pathname }); return; }
    setError("");
    setLoading(true);
    const { error: err } = await supabase.from("reports").insert({
      reported_pro_id: proId || null,
      reporter_id: user.id,
      reason,
      description: details.trim(),
    });
    setLoading(false);
    if (err) { setError("Something went wrong. Please try again."); return; }
    setSubmitted(true);
  };

  /* ── Success state ── */
  if (submitted) {
    return (
      <div style={{ fontFamily: T.sans, background: T.n50, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.n100}`, background: T.white }}>
          <button onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0 }}
            onMouseEnter={e => (e.currentTarget.style.color = T.n800)}
            onMouseLeave={e => (e.currentTarget.style.color = T.n400)}>
            <Icon name="ArrowLeft" size={14} strokeWidth={2} />
            Back
          </button>
        </div>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px" }}>
          <div style={{ maxWidth: 400, textAlign: "center" }}>
            <div style={{ width: 64, height: 64, borderRadius: "50%", background: T.g50, border: `1.5px solid ${T.g100}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}>
              <Icon name="CircleCheck" size={28} color={T.g500} />
            </div>
            <h2 style={{ fontFamily: T.serif, fontSize: "clamp(24px,5vw,30px)", fontWeight: 400, color: T.n900, margin: "0 0 12px", letterSpacing: "-0.03em" }}>
              Report <em style={{ fontStyle: "italic", color: T.g600 }}>received</em>
            </h2>
            <p style={{ fontSize: 14, color: T.n400, lineHeight: 1.7, margin: "0 0 32px" }}>
              Thank you for letting us know. Our Trust and Safety team will review your report and take
              appropriate action. We may follow up if we need more information.
            </p>
            <button
              onClick={() => navigate("/")}
              style={{
                padding: "12px 28px", background: T.g500, color: T.white,
                border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600,
                fontFamily: T.sans, cursor: "pointer", letterSpacing: "0.01em",
              }}
              onMouseEnter={e => (e.currentTarget.style.background = T.g600)}
              onMouseLeave={e => (e.currentTarget.style.background = T.g500)}>
              Back to home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: T.sans, background: T.n50, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <style>{`* { box-sizing: border-box; } textarea:focus { outline: none; }`}</style>

      {/* Nav */}
      <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.n100}`, background: T.white }}>
        <button onClick={() => navigate(-1)} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0 }}
          onMouseEnter={e => (e.currentTarget.style.color = T.n800)}
          onMouseLeave={e => (e.currentTarget.style.color = T.n400)}>
          <Icon name="ArrowLeft" size={14} strokeWidth={2} />
          Back
        </button>
      </div>

      <div style={{ flex: 1, display: "flex", justifyContent: "center", padding: "clamp(32px,5vw,56px) 20px" }}>
        <div style={{ width: "100%", maxWidth: 520 }}>

          {/* Header */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 7, color: T.r500, fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>
              <Icon name="Flag" size={13} color={T.r500} strokeWidth={2} />
              Report
            </div>
            <h1 style={{ fontFamily: T.serif, fontSize: "clamp(28px,5vw,36px)", fontWeight: 400, margin: "0 0 10px", color: T.n900, letterSpacing: "-0.03em", lineHeight: 1.15 }}>
              {loadingPro
                ? "Report a professional"
                : <>Report <em style={{ fontStyle: "italic", color: T.r500 }}>{proName}</em></>}
            </h1>
            <p style={{ fontSize: 14, color: T.n400, margin: 0, lineHeight: 1.65 }}>
              Your report is confidential. Our Trust and Safety team reviews every submission and
              takes action where policies are violated.
            </p>
          </div>

          {/* Card */}
          <div style={{ background: T.white, borderRadius: 20, border: `1px solid ${T.n100}`, padding: "clamp(24px,5vw,36px)", boxShadow: "0 4px 24px rgba(15,17,23,.04)" }}>

            {/* Reason */}
            <div style={{ marginBottom: 28 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: T.n700, display: "block", marginBottom: 12 }}>
                What is the reason for this report? <span style={{ color: T.r500 }}>*</span>
              </label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {REASONS.map(r => (
                  <ReasonCard key={r.id} reason={r} selected={reason} onSelect={setReason} />
                ))}
              </div>
            </div>

            {/* Details */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: T.n700, display: "block", marginBottom: 7 }}>
                Additional details <span style={{ color: T.r500 }}>*</span>
              </label>
              <p style={{ fontSize: 13, color: T.n400, margin: "0 0 10px", lineHeight: 1.5 }}>
                Describe what happened in your own words. Include any relevant dates, times, or booking
                references if you have them.
              </p>
              <textarea
                value={details}
                onChange={e => setDetails(e.target.value.slice(0, CHAR_LIMIT))}
                placeholder="Describe the issue here..."
                rows={5}
                style={{
                  width: "100%", padding: "12px 14px", resize: "vertical",
                  border: `1.5px solid ${T.n150}`, borderRadius: 10,
                  fontSize: 15, fontFamily: T.sans, color: T.n900,
                  background: T.white, lineHeight: 1.6, transition: "border-color .15s",
                }}
                onFocus={e => (e.target.style.borderColor = T.g500)}
                onBlur={e => (e.target.style.borderColor = T.n150)}
              />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                {details.trim().length > 0 && details.trim().length < 20
                  ? <span style={{ fontSize: 12, color: T.r500 }}>Please add a bit more detail (at least 20 characters).</span>
                  : <span />}
                <span style={{ fontSize: 12, color: T.n400, marginLeft: "auto" }}>{details.length} / {CHAR_LIMIT}</span>
              </div>
            </div>

            {/* Safety note */}
            <div style={{ display: "flex", gap: 10, padding: "12px 14px", borderRadius: 10, background: "#fff8e1", border: "1px solid #fde68a", marginBottom: 24 }}>
              <Icon name="AlertTriangle" size={16} color="#b45309" strokeWidth={1.8} />
              <p style={{ fontSize: 13, color: "#78350f", margin: 0, lineHeight: 1.55 }}>
                If you are in immediate danger, please contact the relevant emergency services. Do not
                rely on this form for urgent safety situations.
              </p>
            </div>

            {error && (
              <p style={{ fontSize: 13, color: T.r500, margin: "0 0 16px", padding: "10px 14px", background: T.r100, borderRadius: 8 }}>{error}</p>
            )}

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              style={{
                width: "100%", padding: "14px 0",
                background: canSubmit ? T.r500 : T.n150,
                color: canSubmit ? T.white : T.n400,
                border: "none", borderRadius: 12,
                fontSize: 15, fontWeight: 600, fontFamily: T.sans,
                cursor: canSubmit ? "pointer" : "not-allowed",
                transition: "background .15s", letterSpacing: "0.01em",
              }}
              onMouseEnter={e => { if (canSubmit) e.currentTarget.style.background = "#a93226"; }}
              onMouseLeave={e => { if (canSubmit) e.currentTarget.style.background = T.r500; }}>
              {loading ? "Submitting..." : "Submit report"}
            </button>

            <p style={{ fontSize: 12, color: T.n400, textAlign: "center", margin: "14px 0 0", lineHeight: 1.5 }}>
              Reports are confidential and reviewed by our Trust and Safety team within 2 business days.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}